import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { LoadingButton, TabContext, TabPanel } from '@mui/lab';
import { Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  useAddDeviceMutation,
  useGetAllProductNamesMutation,
  useGetDeviceInfoMutation,
  usePutDeviceNameMutation
} from '../../../services/device/deviceService';
import {
  ContainerTab,
  StyledTab,
  StyledTabList
} from './detailsDeviceServiceStyles';
import Strings from '../../../i18n';
import { AddDeviceBarProps } from './detailsDeviceProps';
import {
  DeviceByLocation,
  RegisteredDevice
} from '../../../types/Device/DeviceInterfaces';
import { DeviceMethodsSideBar } from '../deviceMethodsSideBar/deviceMethodsSideBar';
import {
  StyledContainerFooterSideBar,
  StyledContainerItems,
  StyledTitleSideBar
} from '../../../components/commons/SideBarComponents/sideBarComponentsStyles';
import { SideBarReadOnly } from '../../../components/commons/SideBarComponents/ReadOnlyItems/sideBarReadOnly';
import { SideBarSimpleMethodItem } from '../../../components/commons/SideBarComponents/SimpleMethodItem/sideBarSimpleMethodButtonItem';
import { SideBarEditablePropertyItem } from '../../../components/commons/SideBarComponents/EditablePropertyItem/sideBarEditablePropertyItem';

export const DetailsDevice = ({
  reloadTable,
  device,
  openRightSideBarForMethod,
  openRightSideBar,
  setOpenRightSideBar,
  deviceLocationView
}: AddDeviceBarProps) => {
  const [macAddress, setMacAddress] = useState('');
  const [name, setName] = useState('');
  const [productType, setProductType] = useState('');
  const [editDevice, setEditDevice] = useState(false);
  const [value, setValue] = useState('1');
  const [addDevice, { isLoading, isSuccess }] = useAddDeviceMutation();
  const [getDetails, { data: deviceDetails }] = useGetDeviceInfoMutation();
  const isOldGeneration = device ? device.generation < 5 : false;
  const [selectedDevice, setSelectedDevice] = useState<
    RegisteredDevice | DeviceByLocation
  >();
  const navigate = useNavigate();
  const [getProducts, { data }] = useGetAllProductNamesMutation();

  const [
    putDeviceName,
    {
      isSuccess: isSuccessPutDeviceName,
      isLoading: isLoadingPutDeviceName,
      isError: isErrorPutDeviceName,
      error: errorPutDeviceName
    }
  ] = usePutDeviceNameMutation();

  const handleChangeRole = (event: any) => {
    setProductType(event.target.value.toString());
  };

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
    setEditDevice(false);
  };

  useEffect(() => {
    getProducts('');
  }, []);

  useEffect(() => {
    if (device && openRightSideBar) {
      getDetails(device.deviceId);
      setSelectedDevice(device);
      setMacAddress(device.deviceId);
      setName(device.name);
    }
  }, [openRightSideBar]);

  useEffect(() => {
    if (isSuccess) {
      toast.success(Strings.serverAccess.devices.successAddDevice);
      setOpenRightSideBar(false);
      reloadTable();
    }
  }, [isSuccess]);

  useEffect(() => {
    if (!openRightSideBar) {
      setMacAddress('');
      setProductType('');
      setSelectedDevice(undefined);
    }
  }, [openRightSideBar]);

  const redirectToGetDeviceAnnounce = (id: string) => {
    setOpenRightSideBar(false);
    navigate(`/server-access/devices/getDeviceAnnounce/${id}`);
  };

  const redirectToGetDeviceDebug = (id: string) => {
    setOpenRightSideBar(false);
    navigate(`/server-access/devices/getDebugInfo/${id}`);
  };

  const redirectToGetDeviceMethods = (id: string) => {
    setOpenRightSideBar(false);
    navigate(`/server-access/devices/getDeviceMethods/${id}`);
  };
  const redirectToCustomFiles = (id: string) => {
    setOpenRightSideBar(false);
    navigate(`/server-access/devices/customFiles/${id}`);
  };

  const redirectToRecords = (id: string) => {
    setOpenRightSideBar(false);
    navigate(`/server-access/devices/recordsDevice/${id}`);
  };

  const redirectToFileList = (id: string) => {
    setOpenRightSideBar(false);
    navigate(`/server-access/devices/fileList/${id}`);
  };

  const redirectToDesiredProperties = (id: string) => {
    setOpenRightSideBar(false);
    navigate(`/file-sync-db-reader/${id}`);
  };

  useEffect(() => {
    if (isSuccessPutDeviceName) {
      toast.success(Strings.serverAccess.devices.successPutDeviceName);
      setEditDevice(false);
      reloadTable();
    }
  }, [isSuccessPutDeviceName]);

  useEffect(() => {
    if (isErrorPutDeviceName && errorPutDeviceName) {
      if (
        'data' in errorPutDeviceName &&
        typeof errorPutDeviceName.data === 'object' &&
        errorPutDeviceName.data !== null
      ) {
        const errorData = errorPutDeviceName.data as { title?: string };
        toast.error(errorData.title || 'error');
      }
    }
  }, [isErrorPutDeviceName]);

  return (
    <>
      <StyledContainerItems>
        <StyledTitleSideBar>
          {editDevice
            ? Strings.serverAccess.devices.editDevice
            : Strings.serverAccess.devices.detailsDevice}
        </StyledTitleSideBar>
        <TabContext value={value}>
          {selectedDevice && (
            <StyledTabList
              onChange={handleChange}
              variant="fullWidth"
              indicatorColor="secondary"
              textColor="inherit"
            >
              <StyledTab label="Properties" value="1" key="properties" />
              <StyledTab label="Methods" value="2" key="methods" />
              <StyledTab label="Tables" value="3" key="tables" />
            </StyledTabList>
          )}
          <TabPanel value="1" sx={ContainerTab}>
            {device && editDevice && (
              <SideBarEditablePropertyItem
                item={{
                  label: Strings.devices.name,
                  type: 'inputText',
                  currentValue: name,
                  onChange: (e) => setName(e.target.value)
                }}
              />
            )}
            {device && !editDevice && (
              <SideBarReadOnly
                readOnlyItems={[
                  {
                    name: Strings.devices.name,
                    value: device.name
                  }
                ]}
              />
            )}
            {device && !editDevice && (
              <SideBarReadOnly
                readOnlyItems={[
                  {
                    name: Strings.devices.productType,
                    value:
                      'productType' in device
                        ? device.productType.toString()
                        : ''
                  }
                ]}
              />
            )}
            {!editDevice && (
              <SideBarReadOnly
                readOnlyItems={[
                  {
                    name: Strings.devices.deviceId,
                    value: device?.deviceId.toString() ?? ''
                  }
                ]}
              />
            )}
            {deviceDetails &&
              selectedDevice &&
              !editDevice &&
              deviceDetails.systemMethods.map((method) => {
                return (
                  <SideBarReadOnly
                    key={method.methodName}
                    readOnlyItems={[
                      {
                        name: method.methodName,
                        value: method.methodValue
                      }
                    ]}
                  />
                );
              })}
            <SideBarSimpleMethodItem
              name={
                editDevice
                  ? Strings.common.cancel
                  : Strings.serverAccess.devices.editDevice
              }
              action={() => setEditDevice(!editDevice)}
              successAction={false}
              textSuccess="success"
            />
          </TabPanel>
          <TabPanel value="2" sx={ContainerTab}>
            <DeviceMethodsSideBar
              generation={device ? device.generation : 0}
              macAddress={[selectedDevice?.deviceId?.toString() || '']}
              reloadTable={() => reloadTable()}
              openRightSideBarForMethod={openRightSideBarForMethod}
              deviceIds={[selectedDevice?.deviceId?.toString() || '']}
              idDevice={[selectedDevice?.id ?? 0]}
              openRightSideBar={openRightSideBar}
              setOpenRightSideBar={setOpenRightSideBar}
              deviceLocationView={deviceLocationView}
              hasNotes={
                selectedDevice && 'hasNotes' in selectedDevice
                  ? selectedDevice.hasNotes
                  : false
              }
              productType={
                device && 'productType' in device
                  ? device.productType.toString()
                  : ''
              }
            />
          </TabPanel>
          <TabPanel value="3" sx={ContainerTab}>
            {!isOldGeneration && (
              <SideBarSimpleMethodItem
                name={Strings.serverAccess.devices.FileSyncByDevice}
                action={() =>
                  redirectToDesiredProperties(
                    selectedDevice?.deviceId.toString() || ''
                  )
                }
                successAction={false}
                textSuccess="success"
              />
            )}
            {!isOldGeneration && (
              <SideBarSimpleMethodItem
                name={Strings.serverAccess.devices.getDebugDeviceLabel}
                action={() =>
                  redirectToGetDeviceDebug(
                    selectedDevice?.deviceId.toString() || ''
                  )
                }
                successAction={false}
                textSuccess="success"
              />
            )}
            <SideBarSimpleMethodItem
              name={Strings.serverAccess.devices.getDeviceAnnounce}
              action={() =>
                redirectToGetDeviceAnnounce(
                  selectedDevice?.deviceId.toString() || ''
                )
              }
              successAction={false}
              textSuccess="success"
            />
            {!isOldGeneration && (
              <SideBarSimpleMethodItem
                name={Strings.serverAccess.devices.records}
                action={() =>
                  redirectToRecords(selectedDevice?.deviceId.toString() || '')
                }
                successAction={false}
                textSuccess="success"
              />
            )}
            <SideBarSimpleMethodItem
              name={Strings.serverAccess.devices.scheduleMethods}
              action={() =>
                redirectToGetDeviceMethods(
                  selectedDevice?.deviceId.toString() || ''
                )
              }
              successAction={false}
              textSuccess="success"
            />
            {!isOldGeneration && (
              <SideBarSimpleMethodItem
                name={Strings.serverAccess.devices.customFiles}
                action={() =>
                  redirectToCustomFiles(
                    selectedDevice?.deviceId.toString() || ''
                  )
                }
                successAction={false}
                textSuccess="success"
              />
            )}
            {!isOldGeneration && (
              <SideBarSimpleMethodItem
                name={Strings.serverAccess.devices.fileListLabel}
                action={() =>
                  redirectToFileList(selectedDevice?.deviceId.toString() || '')
                }
                successAction={false}
                textSuccess="success"
              />
            )}
            {!isOldGeneration && (
              <SideBarSimpleMethodItem
                name={Strings.serverAccess.devices.systemState}
                action={() =>
                  navigate(
                    `/server-access/devices/systemState/${
                      selectedDevice?.deviceId.toString() || ''
                    }`
                  )
                }
                successAction={false}
                textSuccess="success"
              />
            )}
            {isOldGeneration && (
              <SideBarSimpleMethodItem
                name={Strings.serverAccess.devices.eventsTable}
                action={() =>
                  navigate(
                    `/server-access/devices/eventTable/${
                      selectedDevice?.deviceId.toString() || ''
                    }`
                  )
                }
                successAction={false}
                textSuccess="success"
              />
            )}
            {isOldGeneration && (
              <SideBarSimpleMethodItem
                name={Strings.serverAccess.devices.actionsTable}
                action={() =>
                  navigate(
                    `/server-access/devices/actionTable/${
                      selectedDevice?.deviceId.toString() || ''
                    }`
                  )
                }
                successAction={false}
                textSuccess="success"
              />
            )}
          </TabPanel>
        </TabContext>
      </StyledContainerItems>
      {value === '1' && (
        <StyledContainerFooterSideBar>
          <button
            type="button"
            className="btn-cancel"
            onClick={() => {
              setOpenRightSideBar(false);
            }}
          >
            {Strings.common.cancel}
          </button>
          {editDevice && (
            <LoadingButton
              className="btn-save"
              disabled={!macAddress || isLoadingPutDeviceName}
              loading={isLoadingPutDeviceName}
              variant="contained"
              onClick={() => {
                putDeviceName({
                  deviceId: selectedDevice?.id.toString() || '',
                  name
                });
              }}
            >
              {Strings.common.save}
            </LoadingButton>
          )}
        </StyledContainerFooterSideBar>
      )}
    </>
  );
};
